import { Formik } from "formik";
import React from "react";

import { updateEventBranding } from "../http/events";
import { BrandingSchema } from "../screens/Schemas";
import AutoSave from "./AutoSave";
import { LogoPreviewAndUploader } from "./UpdateProfileForm";
import { TextField } from "@/components/settings/Field";
import {
  SettingsPage,
  SettingsSection,
} from "@/components/settings/SettingsPage";
import { t } from "@/localization";

const EventBrandingForm = ({ event }) => {
  const {
    slug,
    broadcaster,
    description,
    logo_path,
    company_name,
    company_url,
    custom_date_display,
  } = event;

  return (
    <SettingsPage
      title={t("Branding")}
      description={t(
        "How this event's page looks to guests. It overrides the branding in your profile."
      )}
    >
      <Formik
        initialValues={{
          description,
          logo_path,
          company_name,
          company_url,
          custom_date_display,
        }}
        onSubmit={async (values) => {
          try {
            await updateEventBranding({ slug, ...values });
          } catch (err) {
            console.error(err);
            throw new Error(t("Could not update branding settings"));
          }
        }}
        validationSchema={BrandingSchema}
      >
        {(f) => (
          <>
            <AutoSave {...f} />
            <SettingsSection first title={t("Event page")}>
              <TextField
                label={t("Description")}
                optional
                help="branding.description"
                multiline
                rows={5}
                value={f.values.description}
                onChangeText={f.handleChange("description")}
                onBlur={f.handleBlur("description")}
                placeholder={t("A few words for your guests")}
                hint={t("Supports Markdown: **bold**, lists and links.")}
                error={f.touched.description ? f.errors.description : null}
              />
              <TextField
                label={t("Date shown to guests")}
                optional
                help="branding.customDate"
                value={f.values.custom_date_display}
                onChangeText={f.handleChange("custom_date_display")}
                onBlur={f.handleBlur("custom_date_display")}
                placeholder={t("e.g. 7pm ET / 4pm PT")}
                hint={t(
                  "Replaces the date and time on your event page and in reminder emails."
                )}
                error={
                  f.touched.custom_date_display
                    ? f.errors.custom_date_display
                    : null
                }
                last
              />
            </SettingsSection>

            <SettingsSection title={t("Company")} help="branding.company">
              <TextField
                label={t("Company name")}
                optional
                value={f.values.company_name}
                onChangeText={f.handleChange("company_name")}
                onBlur={f.handleBlur("company_name")}
                placeholder={t("e.g. Anna & Mark Weddings")}
                error={f.touched.company_name ? f.errors.company_name : null}
              />
              <TextField
                label={t("Website")}
                optional
                value={f.values.company_url}
                onChangeText={f.handleChange("company_url")}
                onBlur={f.handleBlur("company_url")}
                placeholder={t("e.g. annaandmark.com")}
                autoCapitalize="none"
                autoCorrect={false}
                error={f.touched.company_url ? f.errors.company_url : null}
                last
              />
            </SettingsSection>

            <SettingsSection title={t("Logo")} help="branding.logo">
              <LogoPreviewAndUploader
                logo_path={f.values.logo_path}
                path={`${broadcaster}/${slug}`}
                disclaimer={t(
                  "Shown on this event page only. PNG or JPG; it's trimmed and resized automatically. Horizontal logos work best. A white logo on a transparent background may not show on a light page."
                )}
                onComplete={async (logo_path) => {
                  await f.setFieldValue("logo_path", logo_path);
                }}
                onDelete={async () => {
                  await f.setFieldValue("logo_path", null);
                }}
              />
            </SettingsSection>
          </>
        )}
      </Formik>
    </SettingsPage>
  );
};

export default EventBrandingForm;
